import { Mail, Shield, Trash2, UserPlus, Users } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmModal,
  Input,
  Pagination,
} from '../components/ui';
import { useAuth, useOrganization, usePage } from '../contexts';
import { organizationInvitationService } from '../services';
import { OrganizationInvitation, OrganizationInvitationStatus, OrganizationMember } from '../types';
import { isOrganizationAdmin } from '../utils/organizationRole';

const statusVariants: Record<string, 'success' | 'warning' | 'secondary' | 'error'> = {
  accepted: 'success',
  pending: 'warning',
  revoked: 'secondary',
  expired: 'error',
};

const roleLabel = (role: string, t: (key: string) => string) =>
  role === 'owner'
    ? t('organizationInvitations.roleOwner')
    : role === 'admin'
    ? t('organizationInvitations.roleAdmin')
    : t('organizationInvitations.roleMember');

const OrganizationInvitations: React.FC = () => {
  const { t } = useTranslation();
  const { setPageInfo } = usePage();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemovingMember, setIsRemovingMember] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<OrganizationMember | null>(null);
  const [invitationPage, setInvitationPage] = useState(1);
  const [invitationTotalPages, setInvitationTotalPages] = useState(1);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [statusFilter, setStatusFilter] = useState<'' | OrganizationInvitationStatus>('');

  const isOrganizationOwner = isOrganizationAdmin(user);

  useEffect(() => {
    setPageInfo(
      t('organizationInvitations.title'),
      t('organizationInvitations.subtitle')
    );
  }, [setPageInfo, t]);

  const getErrorMessage = (error: unknown, fallback: string): string => {
    const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return fallback;
  };

  const loadMembers = useCallback(async () => {
    if (!currentOrganization) return;
    setIsLoadingMembers(true);
    try {
      const response = await organizationInvitationService.listMembers(
        currentOrganization.id,
        {
          page: 1,
          page_size: 20,
        }
      );
      setMembers(response.items || []);
    } catch (error) {
      toast.error(
        getErrorMessage(error, t('organizationInvitations.errorLoadMembers'))
      );
    } finally {
      setIsLoadingMembers(false);
    }
  }, [currentOrganization, t]);

  const loadInvitations = useCallback(async () => {
    if (!currentOrganization) return;
    setIsLoadingInvitations(true);
    try {
      const response = await organizationInvitationService.listInvitations(
        currentOrganization.id,
        {
          status: statusFilter || undefined,
          page: invitationPage,
          page_size: 10,
        }
      );
      setInvitations(response.items || []);
      setInvitationTotalPages(response.total_pages || 1);
    } catch (error) {
      toast.error(
        getErrorMessage(error, t('organizationInvitations.errorLoadInvitations'))
      );
    } finally {
      setIsLoadingInvitations(false);
    }
  }, [currentOrganization, invitationPage, statusFilter, t]);

  useEffect(() => {
    if (!currentOrganization || !isOrganizationOwner) return;
    loadMembers();
  }, [currentOrganization, isOrganizationOwner, loadMembers]);

  useEffect(() => {
    if (!currentOrganization || !isOrganizationOwner) return;
    loadInvitations();
  }, [currentOrganization, isOrganizationOwner, loadInvitations]);

  const handleCreateInvitation = async () => {
    if (!currentOrganization) return;
    if (!email.trim()) {
      toast.error(t('organizationInvitations.emailRequired'));
      return;
    }
    setIsSubmitting(true);
    try {
      await organizationInvitationService.createInvitation(currentOrganization.id, {
        email: email.trim(),
        role,
        expires_in_hours: 24,
      });
      toast.success(t('organizationInvitations.invitationSent'));
      setEmail('');
      setRole('member');
      setInvitationPage(1);
      loadInvitations();
    } catch (error) {
      toast.error(
        getErrorMessage(error, t('organizationInvitations.errorCreateInvitation'))
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: number) => {
    if (!currentOrganization) return;
    try {
      await organizationInvitationService.revokeInvitation(
        currentOrganization.id,
        invitationId
      );
      toast.success(t('organizationInvitations.invitationRevoked'));
      loadInvitations();
    } catch (error) {
      toast.error(
        getErrorMessage(error, t('organizationInvitations.errorRevokeInvitation'))
      );
    }
  };

  const handleRemoveMember = async () => {
    if (!currentOrganization || !memberToRemove) return;
    setIsRemovingMember(true);
    try {
      await organizationInvitationService.removeMember(
        currentOrganization.id,
        memberToRemove.user_id
      );
      toast.success(t('organizationInvitations.memberRemoved'));
      setMemberToRemove(null);
      loadMembers();
    } catch (error) {
      toast.error(
        getErrorMessage(error, t('organizationInvitations.errorRemoveMember'))
      );
    } finally {
      setIsRemovingMember(false);
    }
  };

  if (!currentOrganization) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-600 dark:text-gray-300">
          {t('organizationInvitations.organizationRequired')}
        </CardContent>
      </Card>
    );
  }

  if (!isOrganizationOwner) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Shield className="h-10 w-10 mx-auto mb-3 text-amber-500" />
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t('organizationInvitations.ownerOnly')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary-600" />
            {t('organizationInvitations.sendInvite')}
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder={t('organizationInvitations.emailPlaceholder')}
            leftIcon={<Mail className="h-4 w-4" />}
          />
          <select
            className="select-control"
            value={role}
            onChange={e => setRole(e.target.value as 'member' | 'admin')}
          >
            <option value="member">{t('organizationInvitations.roleMember')}</option>
            <option value="admin">{t('organizationInvitations.roleAdmin')}</option>
          </select>
          <Button variant="primary" onClick={handleCreateInvitation} disabled={isSubmitting}>
            {isSubmitting ? t('common.saving') : t('organizationInvitations.sendInvite')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('organizationInvitations.pendingInvitations')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-end">
            <select
              className="select-control max-w-xs"
              value={statusFilter}
              onChange={e =>
                setStatusFilter((e.target.value || '') as '' | OrganizationInvitationStatus)
              }
            >
              <option value="">{t('organizationInvitations.allStatuses')}</option>
              <option value="pending">{t('organizationInvitations.statusPending')}</option>
              <option value="accepted">{t('organizationInvitations.statusAccepted')}</option>
              <option value="revoked">{t('organizationInvitations.statusRevoked')}</option>
              <option value="expired">{t('organizationInvitations.statusExpired')}</option>
            </select>
          </div>

          {isLoadingInvitations ? (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          ) : invitations.length === 0 ? (
            <p className="text-sm text-gray-500">{t('organizationInvitations.noInvitations')}</p>
          ) : (
            <div className="space-y-2">
              {invitations.map(invite => (
                <div
                  key={invite.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between gap-3"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {invite.email}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {roleLabel(invite.role, t)} -{' '}
                      {new Date(invite.expires_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={statusVariants[invite.status] || 'secondary'}
                      size="sm"
                    >
                      {t(`organizationInvitations.status${invite.status.charAt(0).toUpperCase()}${invite.status.slice(1)}`)}
                    </Badge>
                    {invite.status === 'pending' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRevokeInvitation(invite.id)}
                      >
                        {t('organizationInvitations.revoke')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <Pagination
            currentPage={invitationPage}
            totalPages={invitationTotalPages}
            onPageChange={setInvitationPage}
            disabled={isLoadingInvitations}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary-600" />
            {t('organizationInvitations.members')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingMembers ? (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-gray-500">{t('organizationInvitations.noMembers')}</p>
          ) : (
            <div className="space-y-2">
              {members.map(member => (
                <div
                  key={member.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {member.user_full_name || member.user_email || `#${member.user_id}`}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {member.user_email || '-'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        String(member.role) === 'owner'
                          ? 'warning'
                          : member.role === 'admin'
                          ? 'info'
                          : 'secondary'
                      }
                      size="sm"
                    >
                      {roleLabel(member.role, t)}
                    </Badge>
                    {String(member.role) !== 'owner' && member.user_id !== user?.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setMemberToRemove(member)}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" />
                        {t('organizationInvitations.removeMember')}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmModal
        isOpen={Boolean(memberToRemove)}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleRemoveMember}
        title={t('organizationInvitations.removeMember')}
        message={t('organizationInvitations.confirmRemoveMember', {
          name:
            memberToRemove?.user_full_name ||
            memberToRemove?.user_email ||
            `#${memberToRemove?.user_id}`,
        })}
        confirmText={t('organizationInvitations.removeMember')}
        variant="danger"
        isLoading={isRemovingMember}
      />
    </div>
  );
};

export default OrganizationInvitations;
